import React, { Component } from 'react';
import { withTranslation } from 'react-i18next';
import { compose } from 'redux';
import { connect } from 'react-redux';
import AuditLogsActions from '../redux/AuditLogsRedux';
import { withStyles } from '@material-ui/styles';
import withWidth from '@material-ui/core/withWidth';
import clsx from 'clsx';
import { Colors, StyledComponents, Metrics } from '../themes';
import cloneDeep from 'lodash/cloneDeep';
import Header from '../components/header/Header';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import Table from '../components/common/Table';
import SnackbarNotification from '../components/common/SnackbarNotification';
import { formatDateTime, hackToAddOffset } from '../lib/Utils';
import { subHours } from 'date-fns';
import Immutable from 'seamless-immutable';

const { MainContentContainer } = StyledComponents;
const HOURS_BEFORE = 6; // default number of hours to retrieve data

const styles = (theme) => ({
  gridContainer: {
    [theme.breakpoints.up('xl')]: {
      maxWidth: Metrics.contentGridMaxWidthXl.general,
    },
  },
  paper: {
    textAlign: 'center',
    background: Colors.hoverGrey,
    color: Colors.white,
  },
});

class AuditLogs extends Component {
  constructor(props) {
    super(props);
    this.timer = null;
    this.state = {
      // snackbar
      isSnackbarNotificationOpen: false,
      snackbarVariant: 'warning',
      snackbarMessage: '',
      // date picker
      startTime: subHours(new Date(), HOURS_BEFORE),
      endTime: new Date(),
      // page params
      auditLogsItemsPerPage: 20,
      auditLogsPageNumber: 0,
      auditLogsSearchValue: null,
      auditLogsSortBy: 'timestamp',
      auditLogsSortDirection: 'DESC',
      // tooltip
      copyTooltipTextId: 'clickToCopy',
    };
  }

  componentWillUnmount() {
    this.props.resetAuditLogs();
    if (this.timer) {
      clearTimeout(this.timer);
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.customerId !== this.props.customerId) {
      this.props.resetAuditLogs();
    }
    if (prevProps.isLoading && !this.props.isLoading) {
      if (this.props.errorMessage) {
        this.setState({
          isSnackbarNotificationOpen: true,
          snackbarVariant: 'error',
          snackbarMessage: this.props.errorMessage,
        });
      }
    }
  }

  sendGetAuditLogsRequest = () => {
    const start = hackToAddOffset(this.state.startTime.toJSON()).slice(0, -5);
    const end = hackToAddOffset(this.state.endTime.toJSON()).slice(0, -5);
    const params = {
      start,
      end, // format '2021-06-13T20:59:59',
      items: this.state.auditLogsItemsPerPage,
      page: this.state.auditLogsPageNumber,
      value: this.state.auditLogsSearchValue,
      sort: this.state.auditLogsSortDirection,
      sortBy: this.state.auditLogsSortBy,
    };
    this.props.getAuditLogs(this.props.customerId, params);
  };

  changeAuditLogsItemsPerPage = (auditLogsItemsPerPage) => {
    this.setState({ auditLogsItemsPerPage, auditLogsPageNumber: 0 }, () => this.sendGetAuditLogsRequest());
  };

  changeAuditLogsPage = (auditLogsPageNumber) => {
    this.setState({ auditLogsPageNumber }, () => this.sendGetAuditLogsRequest());
  };

  handleSearchAuditLogs = (auditLogsSearchValue) => {
    this.setState({ auditLogsSearchValue, auditLogsPageNumber: 0 }, () => this.sendGetAuditLogsRequest());
  };

  handleSearchAuditLogsKeyPress = (event, value) => {
    if (event.key === 'Enter') {
      this.handleSearchAuditLogs(value);
    }
  };

  handleSortAuditLogs = (auditLogsSortBy, auditLogsSortDirection) => {
    this.setState({ auditLogsSortBy, auditLogsSortDirection, auditLogsPageNumber: 0 }, () =>
      this.sendGetAuditLogsRequest(),
    );
  };

  copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    this.setState({ copyTooltipTextId: 'textCopied' });
    this.timer = setTimeout(() => {
      this.setState({ copyTooltipTextId: 'clickToCopy' });
    }, 1000);
  };

  render() {
    const { t, classes, width } = this.props;
    const isLarge = width === 'lg' || width === 'xl';
    const rawReportData = cloneDeep(this.props.auditLogs);
    const sortedReportData = Immutable.asMutable(rawReportData).sort((a, b) => -a.timestamp.localeCompare(b.timestamp));
    const reportData = sortedReportData.map((row) => {
      return {
        ...row,
        eventMessageSummary: row.eventMessage.slice(0, 330),
        timestamp: formatDateTime(hackToAddOffset(row.timestamp)),
      };
    });

    const auditLogsColumns = [
      { id: 'timestamp', field: 'timestamp', title: t('common.time') },
      {
        id: 'eventMessage',
        field: 'eventMessage',
        title: t('ReportManager_getEventReport_col5'),
        type: 'copyOverflowText',
      },
      { id: 'username', field: 'username', title: t('auth.username') },
      { id: 'userIPAddr', field: 'userIPAddr', title: t('auditLogs.userIPAddress') },
    ];

    return (
      <MainContentContainer className={classes.MainContentContainer}>
        <SnackbarNotification
          variant={this.state.snackbarVariant}
          open={this.state.isSnackbarNotificationOpen}
          message={this.state.snackbarMessage}
          close={() => this.setState({ isSnackbarNotificationOpen: false })}
        />
        <Header />
        <Grid container className={classes.gridContainer} spacing={isLarge ? 6 : 5}>
          <Grid item xs={12}>
            <Paper className={clsx(classes.paper, classes.reportWidget)}>
              <Table
                // table header
                isAuditLogsHeader
                isThreeElementsHeader
                startTime={this.state.startTime}
                endTime={this.state.endTime}
                handleStartTimeChange={(time) => this.setState({ startTime: time ? time : null })}
                handleEndTimeChange={(time) => this.setState({ endTime: time ? time : null })}
                add2ButtonText={t('reportManager_excecuteReport')}
                onAdd2ButtonClick={this.sendGetAuditLogsRequest}
                data={reportData}
                handleClick={this.copyToClipboard}
                copyTooltipText={t(`common.${this.state.copyTooltipTextId}`)}
                noDataText={t('Global_noData')}
                columns={auditLogsColumns}
                handleSearch={this.handleSearchAuditLogs}
                sortBy={this.state.auditLogsSortBy}
                sortDirection={this.state.auditLogsSortDirection}
                handleSort={this.handleSortAuditLogs}
                searchPlaceholder={t('ReportManager_getEventReport_search')}
                itemsPerPage={this.state.auditLogsItemsPerPage}
                changeItemsPerPage={this.changeAuditLogsItemsPerPage}
                totalElements={this.props.totalElements}
                page={this.state.auditLogsPageNumber}
                changePage={this.changeAuditLogsPage}
                onKeyPress={this.handleSearchAuditLogsKeyPress}
                // rows per page select props
                selectData={[5, 10, 20]}
                selectLabel={t('table.showPerPage')}
                selectValue={this.state.auditLogsItemsPerPage}
                onSelect={this.changeAuditLogsItemsPerPage}
                selectWidth={90}
                copyable
              />
            </Paper>
          </Grid>
        </Grid>
      </MainContentContainer>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    customerId: state.auth.customerId,
    customerIdSub: state.auth.customerIdSub,
    // Audit logs:
    isLoading: state.auditLogs.isLoading,
    auditLogs: state.auditLogs.auditLogs,
    errorMessage: state.auditLogs.errorMessage,
    totalElements: state.auditLogs.totalElements,
  };
};

const mapDispatchToProps = (dispatch) => ({
  // Audit logs
  getAuditLogs: (customerId, params) => dispatch(AuditLogsActions.getAuditLogsRequest(customerId, params)),
  resetAuditLogs: () => dispatch(AuditLogsActions.resetAuditLogs()),
});

export default compose(
  withTranslation(),
  withStyles(styles),
  withWidth(),
  connect(mapStateToProps, mapDispatchToProps),
)(AuditLogs);
